<script setup lang="ts">
import { ArrowLeft, ArrowRight } from "lucide-vue-next";

type CategoryCarouselLink = {
  key: string;
  label: string;
  to: string | Record<string, unknown>;
  icon: string | null;
};

const props = defineProps<{
  title: string;
  links: CategoryCarouselLink[];
}>();

const trackRef = useTemplateRef<HTMLElement>("trackRef");
const groupRef = useTemplateRef<HTMLElement>("groupRef");

let autoplayFrame = 0;
let nudgeFrame = 0;
let lastFrameTime = 0;
let offset = 0;
let manualPauseUntil = 0;

const SPEED_PX_PER_MS = 0.036;
const MANUAL_PAUSE_MS = 5200;

function getLoopWidth() {
  return groupRef.value?.offsetWidth || 0;
}

function applyOffset(nextOffset = offset) {
  const track = trackRef.value;
  const loopWidth = getLoopWidth();
  if (!track || !loopWidth) return;

  offset = nextOffset;

  while (offset >= loopWidth) offset -= loopWidth;
  while (offset < 0) offset += loopWidth;

  track.style.transform = `translate3d(${-offset}px, 0, 0)`;
}

function getStep() {
  const firstCard = groupRef.value?.firstElementChild as HTMLElement | null;
  return firstCard ? firstCard.offsetWidth + 12 : 230;
}

function runAutoplay(timestamp: number) {
  if (!lastFrameTime) lastFrameTime = timestamp;
  const delta = Math.min(timestamp - lastFrameTime, 50);
  lastFrameTime = timestamp;

  if (
    trackRef.value &&
    getLoopWidth() &&
    timestamp >= manualPauseUntil &&
    !document.hidden
  ) {
    applyOffset(offset + delta * SPEED_PX_PER_MS);
  }

  autoplayFrame = requestAnimationFrame(runAutoplay);
}

function startAutoplay() {
  if (!import.meta.client) return;
  if (props.links.length < 2) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  stopAutoplay();
  lastFrameTime = 0;
  autoplayFrame = requestAnimationFrame(runAutoplay);
}

function stopAutoplay() {
  if (autoplayFrame) cancelAnimationFrame(autoplayFrame);
  autoplayFrame = 0;
}

function pauseTemporarily() {
  if (!import.meta.client) return;
  manualPauseUntil = performance.now() + MANUAL_PAUSE_MS;
}

function resumeSoon() {
  if (!import.meta.client) return;
  manualPauseUntil = Math.min(manualPauseUntil, performance.now() + 300);
}

function nudge(direction: 1 | -1) {
  if (!import.meta.client) return;

  pauseTemporarily();
  if (nudgeFrame) cancelAnimationFrame(nudgeFrame);

  const startOffset = offset;
  const distance = getStep() * direction;
  const startedAt = performance.now();
  const duration = 360;

  const animate = (timestamp: number) => {
    const progress = Math.min(1, (timestamp - startedAt) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    applyOffset(startOffset + distance * eased);

    if (progress < 1) {
      nudgeFrame = requestAnimationFrame(animate);
    } else {
      nudgeFrame = 0;
    }
  };

  nudgeFrame = requestAnimationFrame(animate);
}

function reset() {
  requestAnimationFrame(() => applyOffset(0));
}

onMounted(() => {
  reset();
  startAutoplay();

  window.addEventListener("resize", reset);
  document.addEventListener("visibilitychange", startAutoplay);
});

onBeforeUnmount(() => {
  stopAutoplay();
  if (nudgeFrame) cancelAnimationFrame(nudgeFrame);
  window.removeEventListener("resize", reset);
  document.removeEventListener("visibilitychange", startAutoplay);
});
</script>

<template>
  <section v-if="links.length" class="mt-10 md:mt-12">
    <div class="flex items-end justify-between gap-5">
      <h2 class="font-gabarito text-2xl font-bold leading-tight text-app-text">
        {{ title }}
      </h2>

      <div
        v-if="links.length > 1"
        class="hidden shrink-0 items-center gap-2 md:flex"
        aria-label="Category carousel controls"
      >
        <button
          type="button"
          class="flex size-10 items-center justify-center rounded-full border border-app-border bg-white text-app-text shadow-[0_6px_18px_rgba(16,24,40,0.06)] transition duration-300 hover:border-primary/35 hover:text-primary"
          aria-label="Previous categories"
          @click="nudge(-1)"
        >
          <ArrowLeft class="size-4" />
        </button>
        <button
          type="button"
          class="flex size-10 items-center justify-center rounded-full border border-app-border bg-white text-app-text shadow-[0_6px_18px_rgba(16,24,40,0.06)] transition duration-300 hover:border-primary/35 hover:text-primary"
          aria-label="Next categories"
          @click="nudge(1)"
        >
          <ArrowRight class="size-4" />
        </button>
      </div>
    </div>

    <div class="relative mt-6">
      <div
        class="overflow-hidden pb-1"
        @focusin="pauseTemporarily"
        @focusout="resumeSoon"
        @pointerdown="pauseTemporarily"
        @wheel="pauseTemporarily"
        @touchstart.passive="pauseTemporarily"
      >
        <div ref="trackRef" class="flex w-max will-change-transform">
          <div ref="groupRef" class="flex shrink-0 gap-3 pr-3">
            <NuxtLink
              v-for="link in links"
              :key="link.key"
              :to="link.to"
              class="group flex min-h-[82px] w-[210px] shrink-0 items-center gap-3 rounded-xl border border-app-border bg-white px-3.5 py-3 transition duration-300 hover:border-primary-200 hover:shadow-[0_4px_18px_rgba(16,24,40,0.05)] sm:w-[230px] lg:w-[218px] xl:w-[224px]"
            >
              <span class="flex size-10 shrink-0 items-center justify-center">
                <img
                  v-if="link.icon"
                  :src="link.icon"
                  alt=""
                  class="size-10 object-contain transition-transform duration-300 group-hover:-translate-y-0.5"
                  aria-hidden="true"
                />
              </span>
              <span
                class="text-sm font-bold leading-[1.2] text-app-text transition-colors duration-300 group-hover:text-primary"
              >
                {{ link.label }}
              </span>
            </NuxtLink>
          </div>

          <div class="flex shrink-0 gap-3 pr-3" aria-hidden="true">
            <NuxtLink
              v-for="link in links"
              :key="`duplicate-${link.key}`"
              :to="link.to"
              tabindex="-1"
              class="group flex min-h-[82px] w-[210px] shrink-0 items-center gap-3 rounded-xl border border-app-border bg-white px-3.5 py-3 transition duration-300 hover:border-primary-200 hover:shadow-[0_4px_18px_rgba(16,24,40,0.05)] sm:w-[230px] lg:w-[218px] xl:w-[224px]"
            >
              <span class="flex size-10 shrink-0 items-center justify-center">
                <img
                  v-if="link.icon"
                  :src="link.icon"
                  alt=""
                  class="size-10 object-contain"
                  aria-hidden="true"
                />
              </span>
              <span class="text-sm font-bold leading-[1.2] text-app-text">
                {{ link.label }}
              </span>
            </NuxtLink>
          </div>
        </div>
      </div>

      <div
        class="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent md:w-12"
        aria-hidden="true"
      />
      <div
        class="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent md:w-12"
        aria-hidden="true"
      />
    </div>
  </section>
</template>
